import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { useToast } from '../context/ToastContext';

// Customers (the director, 14 Oct 2026): everything printed under "Invoice To"
// and the terms an invoice starts from. Below it, the price list as it stands --
// read only: each price lives on its part or product (15 Oct 2026, one source),
// set there or in the Bulk Tool, and each part's row opens it.

const TEAL  = '#14b8a6';
const BORD  = '#2a3040';
const CARD  = '#1e2329';
const DARK  = '#0f1117';
const MUTED = '#6b7280';
const TEXT  = '#f0f2f5';
const AMBER = '#f59e0b';

const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 10px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };
const label = { fontSize: '10px', fontWeight: '700', color: MUTED, marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' };
const btn = (primary) => ({
  background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL,
  border: `1px solid ${primary ? TEAL : 'rgba(20,184,166,0.6)'}`, borderRadius: '8px', padding: '7px 14px',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap',
});

// "0,2181" or "0.2181" both mean the same price.
const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const fmtPrice = (p) => (p === null || p === undefined ? '' : `€ ${Number(p).toFixed(4).replace('.', ',')}`);

export default function Customers() {
  const goBack = useBack('/admin');
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [prices, setPrices] = useState(null);
  const [editing, setEditing] = useState(null);      // the customer form while editing
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const loadList = () => api.get('/api/v1/customers').then(r => setCustomers(r.data || [])).catch(() => {});
  const loadOne = (cid) => api.get(`/api/v1/customers/${cid}`).then(r => setCustomer(r.data)).catch(err => toast.error(err.response?.data?.error || 'Could not load the customer'));

  useEffect(() => {
    loadList();
    api.get('/api/v1/prices').then(r => setPrices(r.data || [])).catch(() => setPrices([]));
  }, []); // eslint-disable-line

  // Open the one in the address, or the first when there is only one.
  useEffect(() => {
    if (id) { loadOne(id); return; }
    if (customers.length === 1) navigate(`/admin/customers/${customers[0].id}`, { replace: true });
  }, [id, customers.length]); // eslint-disable-line

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = (prices || []).filter(r => !r.retired);
    return q ? all.filter(r => [r.number, r.printed_name, r.name].some(v => String(v || '').toLowerCase().includes(q))) : all;
  }, [prices, filter]);
  const unpriced = (prices || []).filter(r => !r.retired && (r.invoice_price === null || r.invoice_price === undefined)).length;

  async function saveCustomer() {
    setSaving(true);
    try {
      const body = { ...editing, payment_days: Number(editing.payment_days), transport_price: toNum(editing.transport_price) };
      const { data } = editing.id
        ? await api.put(`/api/v1/customers/${editing.id}`, body)
        : await api.post('/api/v1/customers', body);
      setEditing(null);
      toast.success(editing.id ? 'Customer saved' : 'Customer added');
      await loadList();
      if (editing.id) loadOne(data.id); else navigate(`/admin/customers/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the customer');
    } finally {
      setSaving(false);
    }
  }

  const c = customer;
  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px' }}>Customers</h1>
          <p style={{ color: MUTED, fontSize: '13px', margin: 0 }}>Who you invoice, and what they call each part — with its price</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {customers.map(x => (
            <button key={x.id} onClick={() => navigate(`/admin/customers/${x.id}`)}
              style={{ ...btn(String(x.id) === String(id)), fontWeight: '600' }}>{x.name}</button>
          ))}
          <button style={btn(false)} onClick={() => setEditing({ name: '', name_line2: '', address_lines: '', tax_number: '', country_code: '', payment_days: 30, transport_price: '', transport_unit: 'LKW', transport_mode: 'LKW - Lorry', goods_code: '', early_payment_note: true, order_reference_note: '' })}>
            <i className="fa-solid fa-plus" style={{ fontSize: '11px', marginRight: '5px' }} />Add customer
          </button>
        </div>
      </div>

      {editing && (
        <div style={{ background: CARD, border: `1px solid ${TEAL}`, borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>{editing.id ? `Edit ${editing.name}` : 'New customer'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            <div><span style={label}>Name (as on the invoice)</span><input style={inp} value={editing.name} onChange={e => setEditing(x => ({ ...x, name: e.target.value }))} /></div>
            <div><span style={label}>Second line</span><input style={inp} value={editing.name_line2 || ''} onChange={e => setEditing(x => ({ ...x, name_line2: e.target.value }))} placeholder="J. Ravnak GmbH & Co. KG" /></div>
            <div><span style={label}>Address (one line each)</span>
              <textarea rows={2} style={{ ...inp, resize: 'vertical' }} value={Array.isArray(editing.address_lines) ? editing.address_lines.join('\n') : editing.address_lines || ''}
                onChange={e => setEditing(x => ({ ...x, address_lines: e.target.value }))} placeholder={'Borsigstraße 49\n52525 Heinsberg, Germany'} />
            </div>
            <div><span style={label}>Tax number</span><input style={inp} value={editing.tax_number || ''} onChange={e => setEditing(x => ({ ...x, tax_number: e.target.value }))} placeholder="DE811327946" /></div>
            <div><span style={label}>Payment days after the invoice date</span><input type="number" min="0" style={inp} value={editing.payment_days} onChange={e => setEditing(x => ({ ...x, payment_days: e.target.value }))} /></div>
            <div><span style={label}>Transport price, per lorry (€)</span><input style={inp} value={editing.transport_price ?? ''} onChange={e => setEditing(x => ({ ...x, transport_price: e.target.value }))} placeholder="1680" /></div>
            <div><span style={label}>Means of transport</span><input style={inp} value={editing.transport_mode || ''} onChange={e => setEditing(x => ({ ...x, transport_mode: e.target.value }))} placeholder="LKW - Lorry" /></div>
            <div><span style={label}>Goods code</span><input style={inp} value={editing.goods_code || ''} onChange={e => setEditing(x => ({ ...x, goods_code: e.target.value }))} placeholder="39269099" /></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>On invoices, after the order number (a new line starts the second printed line)</span>
              <textarea rows={2} style={{ ...inp, resize: 'vertical' }} value={editing.order_reference_note || ''}
                onChange={e => setEditing(x => ({ ...x, order_reference_note: e.target.value }))}
                placeholder={'Rolling Forecast\n(Email order by B. Claßen)'} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '13px', color: '#d1d5db', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!editing.early_payment_note} onChange={e => setEditing(x => ({ ...x, early_payment_note: e.target.checked }))} />
            Print "2% discount … for payments made within 21 days" on invoices (can be switched off on each invoice)
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
            <button style={{ ...btn(false), color: '#9ca3af', borderColor: '#374151' }} onClick={() => setEditing(null)}>Cancel</button>
            <button style={btn(true)} disabled={saving} onClick={saveCustomer}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {!c && !editing && (
        <div style={{ color: MUTED, fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>
          {customers.length ? 'Choose a customer above.' : 'No customers yet. Add the first one.'}
        </div>
      )}

      {c && (
        <>
          <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', color: TEAL, marginBottom: '4px' }}>Invoice To</div>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>{c.name}</div>
              {c.name_line2 && <div style={{ fontSize: '12px', fontWeight: '700', color: '#d1d5db' }}>{c.name_line2}</div>}
              {(c.address_lines || []).map((l, i) => <div key={i} style={{ fontSize: '12px', color: '#d1d5db' }}>{l}</div>)}
              {c.tax_number && <div style={{ fontSize: '12px', color: '#d1d5db', marginTop: '3px' }}><b>TAX Number:</b> {c.tax_number}</div>}
            </div>
            <div style={{ fontSize: '12.5px', color: '#d1d5db', lineHeight: 1.7 }}>
              <div><span style={{ color: MUTED }}>Due:</span> {c.payment_days} days after the invoice date</div>
              {c.order_reference_note && <div><span style={{ color: MUTED }}>After the order number:</span> {String(c.order_reference_note).split('\n').join(' · ')}</div>}
              <div><span style={{ color: MUTED }}>Transport:</span> {c.transport_price != null ? `€ ${Number(c.transport_price).toFixed(2).replace('.', ',')} per ${c.transport_unit}` : 'no usual price'} · {c.transport_mode}</div>
              <div><span style={{ color: MUTED }}>Goods code:</span> {c.goods_code || '—'}</div>
              <div><span style={{ color: MUTED }}>2% early payment line:</span> {c.early_payment_note ? 'on' : 'off'}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
              <button style={btn(false)} onClick={() => setEditing({ ...c })}><i className="fa-solid fa-pen" style={{ fontSize: '11px', marginRight: '5px' }} />Edit details</button>
            </div>
          </div>

          <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700' }}>Prices <i className="fa-solid fa-lock" title="Directors only" style={{ fontSize: '11px', color: MUTED, marginLeft: '4px' }} /></div>
                <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
                  {prices === null ? 'Loading…' : `${rows.length} on the price list`}
                  {unpriced > 0 && <span style={{ color: AMBER }}> · {unpriced} without a price yet</span>}
                  {' '}· set each on its part or product, or many at once in the Bulk Tool
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input style={{ ...inp, width: '200px' }} placeholder="Search number or name…" value={filter} onChange={e => setFilter(e.target.value)} />
                <button style={btn(false)} onClick={() => navigate('/admin/bulk')}><i className="fa-solid fa-list-check" style={{ fontSize: '11px', marginRight: '5px' }} />Bulk Tool</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '640px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: MUTED, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    <th style={{ padding: '6px 8px', width: '90px' }}>Number</th>
                    <th style={{ padding: '6px 8px' }}>Printed on the invoice</th>
                    <th style={{ padding: '6px 8px', width: '60px' }}>Unit</th>
                    <th style={{ padding: '6px 8px', width: '110px', textAlign: 'right' }}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={`${r.kind}:${r.id}`} onClick={() => r.kind === 'part' && navigate(`/admin/parts/${r.id}`)}
                      style={{ borderTop: `1px solid ${BORD}`, cursor: r.kind === 'part' ? 'pointer' : 'default' }}>
                      <td style={{ padding: '7px 8px', fontVariantNumeric: 'tabular-nums' }}>{r.number || <span style={{ color: MUTED }}>—</span>}</td>
                      <td style={{ padding: '7px 8px' }}>
                        {r.kind === 'product' && <span style={{ fontSize: '9px', fontWeight: '700', color: TEAL, border: `1px solid ${TEAL}`, borderRadius: '3px', padding: '0 4px', marginRight: '6px' }}>PRODUCT</span>}
                        <span style={{ fontWeight: '600' }}>{r.printed_name}</span>
                        {r.invoice_name && <span style={{ color: MUTED, fontSize: '11px', marginLeft: '6px' }}>in the app: {r.name}</span>}
                      </td>
                      <td style={{ padding: '7px 8px' }}>{r.invoice_unit}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {r.invoice_price === null || r.invoice_price === undefined ? <span style={{ color: AMBER, fontSize: '12px' }}>no price</span> : fmtPrice(r.invoice_price)}
                      </td>
                    </tr>
                  ))}
                  {prices !== null && !rows.length && (
                    <tr><td colSpan={4} style={{ padding: '18px 8px', color: MUTED, textAlign: 'center' }}>
                      {filter ? 'Nothing matches that search.' : 'No prices yet. Set them on each part and product, or in the Bulk Tool (Parts or Products → Price).'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
